import { useLazyQuery } from "@apollo/client";
import dayjs from "dayjs";
import { useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "@entry/navigation";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import {
  BeneficiaryType,
  DateFormat,
  FlexibilityPreference,
  GoalType,
  Routes,
  SuggestedAccountTypeQuery,
  SuggestedAccountTypeQueryVariables,
  SuggestedAccountWho,
} from "@ctypes";
import { EntryStepLayout } from "@entry/EntryStepLayout";
import { SUGGESTED_ACCOUNT_TYPE } from "@graphql";
import { useGoalFlow } from "@hooks";
import { Translations, i18n } from "@i18n";
import { AxisLabel, FilledLine, Rail, SliderCard, Stop, StopRow, Thumb } from "./styles";

/**
 * Ported from mobile TaxFlexibilityPreferenceScreen: a discrete
 * tax-advantage ↔ flexibility scale that feeds the backend's
 * suggestedAccountType recommendation (pre-warmed here so the account
 * screen renders instantly).
 */

const BENEFICIARY_TYPE_TO_WHO: Record<BeneficiaryType, SuggestedAccountWho> = {
  [BeneficiaryType.Child]: SuggestedAccountWho.Child,
  [BeneficiaryType.Self]: SuggestedAccountWho.Myself,
  [BeneficiaryType.Adult]: SuggestedAccountWho.Together,
};

const OPTIONS: Array<{ value: FlexibilityPreference; label: string }> = [
  {
    value: FlexibilityPreference.VeryTaxAdvantaged,
    label: i18n.t(Translations.TaxFlexibilityOption1),
  },
  {
    value: FlexibilityPreference.SomewhatTaxAdvantaged,
    label: i18n.t(Translations.TaxFlexibilityOption2),
  },
  {
    value: FlexibilityPreference.SomewhatFlexible,
    label: i18n.t(Translations.TaxFlexibilityOption3),
  },
  {
    value: FlexibilityPreference.VeryFlexible,
    label: i18n.t(Translations.TaxFlexibilityOption4),
  },
];

export const TaxFlexibilityRoute = () => {
  const navigate = useNavigate();
  const { goalProps, setGoalProps } = useGoalFlow();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const railRef = useRef<HTMLDivElement>(null);
  const stopRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const dragPointerId = useRef<number | null>(null);

  const [snapPositions, setSnapPositions] = useState<number[]>([]);
  const [thumbY, setThumbY] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      setSnapPositions(
        stopRefs.current.map((el) => (el ? el.offsetTop + el.offsetHeight / 2 : 0)),
      );
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const displayY = isDragging ? thumbY : (snapPositions[selectedIndex] ?? null);

  const nearestIndex = (y: number) =>
    snapPositions.reduce(
      (closest, pos, index) =>
        Math.abs(pos - y) < Math.abs(snapPositions[closest] - y) ? index : closest,
      0,
    );

  const positionFromPointer = (clientY: number) => {
    if (!railRef.current || snapPositions.length === 0) return null;

    const top = railRef.current.getBoundingClientRect().top;
    const min = snapPositions[0];
    const max = snapPositions[snapPositions.length - 1];

    return Math.max(min, Math.min(clientY - top, max));
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const y = positionFromPointer(e.clientY);
    if (y == null) return;

    e.currentTarget.setPointerCapture(e.pointerId);
    dragPointerId.current = e.pointerId;
    setIsDragging(true);
    setThumbY(y);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragPointerId.current !== e.pointerId) return;

    const y = positionFromPointer(e.clientY);
    if (y != null) setThumbY(y);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (dragPointerId.current !== e.pointerId) return;

    dragPointerId.current = null;
    setIsDragging(false);
    if (thumbY != null) setSelectedIndex(nearestIndex(thumbY));
  };

  const [fetchSuggestedAccountType] = useLazyQuery<
    SuggestedAccountTypeQuery,
    SuggestedAccountTypeQueryVariables
  >(SUGGESTED_ACCOUNT_TYPE);

  const handleSubmit = () => {
    const flexibilityPreference = OPTIONS[selectedIndex].value;
    const who = BENEFICIARY_TYPE_TO_WHO[goalProps.beneficiaryType ?? BeneficiaryType.Child];

    // Warm the cache so the account screen has the recommendation ready.
    fetchSuggestedAccountType({
      variables: {
        input: {
          who,
          goalType: goalProps.goalType ?? GoalType.Education,
          flexibilityPreference,
          beneficiaryDateOfBirth: goalProps.dateOfBirth
            ? dayjs(goalProps.dateOfBirth).format(DateFormat.User)
            : undefined,
        },
      },
    });

    setGoalProps({ flexibilityPreference });
    navigate(Routes.AccountType);
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.TaxFlexibilityTitle),
    subtitle: i18n.t(Translations.TaxFlexibilitySubtitle),
    cta: {
      primary: {
        title: i18n.t(Translations.TaxFlexibilityPrimaryCTA),
        onClick: handleSubmit,
      },
    },
  };

  return (
    <EntryStepLayout {...props}>
      <SliderCard role="radiogroup" aria-label={i18n.t(Translations.TaxFlexibilityTitle)}>
        <AxisLabel type="button" onClick={() => setSelectedIndex(0)}>
          {i18n.t(Translations.TaxFlexibilityTopLabel)}
        </AxisLabel>
        <Rail
          ref={railRef}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
        >
          {displayY != null && (
            <>
              <FilledLine $height={displayY} />
              <Thumb $y={displayY} $dragging={isDragging} />
            </>
          )}
          <StopRow>
            {OPTIONS.map((option, index) => (
              <Stop
                key={option.value}
                ref={(el) => {
                  stopRefs.current[index] = el;
                }}
                type="button"
                role="radio"
                aria-checked={selectedIndex === index}
                $selected={selectedIndex === index}
                onClick={() => setSelectedIndex(index)}
              >
                {option.label}
              </Stop>
            ))}
          </StopRow>
        </Rail>
        <AxisLabel type="button" onClick={() => setSelectedIndex(OPTIONS.length - 1)}>
          {i18n.t(Translations.TaxFlexibilityBottomLabel)}
        </AxisLabel>
      </SliderCard>
    </EntryStepLayout>
  );
};
